/**
 * Draws the coverage footprint of the selected satellite (the ground area it can
 * see) and keeps it in sync as the satellite moves. Clears when nothing — or a
 * non-satellite — is selected.
 */

import { useEffect, useRef } from 'react';
import type { Track } from '@vtak/shared';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { useEntitySelectionStore } from '@/stores/entity-selection-store';
import { usePluginDataStore } from '@/stores/plugin-data-store';
import { CLIENT_CONFIG } from '@/config';
import { computeHorizonFootprint } from '@/map/coverage/footprint-geometry';
import { showFootprint, clearFootprint } from '@/map/coverage/footprint-renderer';
import { SATELLITE_LAYER } from '@/map/coverage/coverage-frame';

/** Subscribe the coverage footprint to the current satellite selection. */
export function useCoverageFootprint(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  const lastKey = useRef('');

  useEffect(() => {
    const update = (): void => updateFootprint(mapRef, modeRef, lastKey);
    const unsubSelection = useEntitySelectionStore.subscribe(update);
    const unsubData = usePluginDataStore.subscribe(update);
    return () => {
      unsubSelection();
      unsubData();
      clearFootprint();
    };
  }, []);
}

/** Render the footprint for the selected satellite, skipping unchanged state. */
function updateFootprint(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
  lastKey: React.MutableRefObject<string>,
): void {
  const map = mapRef.current;
  if (!map) return;

  const satellite = selectedSatellite();
  if (!satellite) {
    if (lastKey.current) {
      clearFootprint();
      lastKey.current = '';
    }
    return;
  }

  const key = `${satellite.uid}:${satellite.lat.toFixed(3)}:${satellite.lon.toFixed(3)}:${modeRef.current}`;
  if (key === lastKey.current) return;
  lastKey.current = key;

  const altitudeKm = typeof satellite.props['altitudeKm'] === 'number' ? satellite.props['altitudeKm'] : 0;
  const ring = computeHorizonFootprint(satellite.lat, satellite.lon, altitudeKm);
  const cone = {
    lat: satellite.lat,
    lng: satellite.lon,
    altitudeMeters: altitudeKm * 1000 * CLIENT_CONFIG.satelliteShellExaggeration,
  };
  showFootprint(map, modeRef.current, ring, cone);
}

/** The selected entity if it is a satellite track, else undefined. */
function selectedSatellite(): Track | undefined {
  const uid = useEntitySelectionStore.getState().selectedUid;
  if (!uid) return undefined;
  const tracks = usePluginDataStore.getState().data.get(SATELLITE_LAYER) as Track[] | undefined;
  return tracks?.find((track) => track.uid === uid);
}
